import React, { useEffect, useState } from "react";
import { PawPrint, Sparkles, Check, ChevronDown, Dna, Lightbulb, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Spinner } from "@/components/kit";

export default function BreedResultCard({ result, dogs = [] }) {
  const [dogId, setDogId] = useState(result?.dog_id || (dogs[0]?.id ?? ""));
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(false);

  useEffect(() => {
    setTips(null);
    setTipsLoading(false);
  }, [result?.id]);

  const loadTips = async () => {
    if (tipsLoading || tips) return;
    setTipsLoading(true);
    try {
      const r = await apiFetch(`/breed-tips?breed=${encodeURIComponent(result.primary_breed)}`);
      setTips(r.tips || []);
    } catch {
      setTips([]);
    } finally {
      setTipsLoading(false);
    }
  };

  useEffect(() => {
    if (!dogId && dogs.length) setDogId(dogs[0].id);
  }, [dogs, dogId]);

  const isUnclear = !result?.primary_breed || result.primary_breed.toLowerCase() === "unclear";

  const saveToDog = async () => {
    if (!dogId || saving) return;
    setSaving(true);
    try {
      await apiFetch(`/dogs/${dogId}/breed`, {
        method: "PATCH",
        body: JSON.stringify({ breed: result.primary_breed }),
      });
      const dog = dogs.find((d) => d.id === dogId);
      setSavedTo(dog?.name || "your dog");
      toast.success(`Saved "${result.primary_breed}" to ${dog?.name || "your dog"}'s profile.`);
    } catch (e) {
      toast.error(e.message || "Could not save breed.");
    } finally {
      setSaving(false);
    }
  };

  const [photoSaving, setPhotoSaving] = useState(false);
  const [photoSaved, setPhotoSaved] = useState(false);
  const saveAsPhoto = async () => {
    if (!dogId || photoSaving || !result.image_base64) return;
    setPhotoSaving(true);
    try {
      await apiFetch(`/dogs/${dogId}/photo`, {
        method: "PATCH",
        body: JSON.stringify({ photo_base64: result.image_base64 }),
      });
      const dog = dogs.find((d) => d.id === dogId);
      setPhotoSaved(true);
      toast.success(`Set as ${dog?.name || "your dog"}'s profile photo.`);
    } catch (e) {
      toast.error(e.message || "Could not set photo.");
    } finally {
      setPhotoSaving(false);
    }
  };

  return (
    <div className="mt-5 rounded-3xl border-2 border-[#E8E3DA] bg-white p-5" data-testid="breed-result">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#F6DED5] flex items-center justify-center shrink-0">
          <PawPrint size={22} color="#E86A45" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-extrabold uppercase tracking-wide text-[#B54828]">
            {result.is_mixed ? "Likely mixed breed" : "Best match"}
          </p>
          <p className="font-display text-2xl font-extrabold text-[#2D2B2A] truncate" data-testid="breed-result-primary">
            {result.primary_breed}
          </p>
        </div>
      </div>

      {result.summary && <p className="text-[#2D2B2A] mt-3 leading-relaxed">{result.summary}</p>}

      {/* Confidence bars */}
      {result.matches?.length > 0 && (
        <div className="mt-4 space-y-2.5">
          {result.matches.map((m, i) => (
            <div key={i} data-testid={`breed-match-${i}`}>
              <div className="flex items-center justify-between text-sm font-bold text-[#2D2B2A] mb-1">
                <span className="truncate">{m.breed}</span>
                <span className="text-[#6B6560] tabular-nums">{m.confidence}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-[#F2EFE9] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#E86A45] transition-all"
                  style={{ width: `${Math.max(4, Math.min(100, m.confidence))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Care notes */}
      {result.care_notes?.length > 0 && (
        <div className="mt-4 bg-[#F8F5F0] rounded-2xl p-4">
          <p className="text-xs font-extrabold uppercase tracking-wide text-[#6B6560] mb-2">Care notes</p>
          <ul className="space-y-1.5">
            {result.care_notes.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#2D2B2A]">
                <Check size={15} color="#6F8F72" className="mt-0.5 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Save to a dog's profile */}
      {!isUnclear && dogs.length > 0 && (
        savedTo ? (
          <div className="mt-4 flex items-center gap-2 text-sm font-bold text-[#4E7150]" data-testid="breed-saved-note">
            <Check size={16} /> Saved to {savedTo}&apos;s profile
          </div>
        ) : (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <select
                data-testid="breed-save-dog-select"
                value={dogId}
                onChange={(e) => setDogId(e.target.value)}
                className="w-full appearance-none text-sm font-bold bg-[#F2EFE9] rounded-full pl-4 pr-9 py-3 outline-none cursor-pointer"
              >
                {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" color="#4A4746" />
            </div>
            <button
              data-testid="breed-save-btn"
              onClick={saveToDog}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#6F8F72] text-white font-extrabold px-5 py-3 hover:brightness-105 disabled:opacity-60 transition-all"
            >
              <Sparkles size={16} /> {saving ? "Saving…" : "Save breed to profile"}
            </button>
          </div>
        )
      )}

      {/* Use photo as profile picture */}
      {!isUnclear && result.image_base64 && dogs.length > 0 && (
        photoSaved ? (
          <div className="mt-2 flex items-center gap-2 text-sm font-bold text-[#4E7150]" data-testid="breed-photo-saved-note">
            <Check size={16} /> Set as profile photo
          </div>
        ) : (
          <button
            data-testid="breed-use-photo-btn"
            onClick={saveAsPhoto}
            disabled={photoSaving}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F2EFE9] text-[#4A4746] font-extrabold px-5 py-3 hover:bg-[#E8E3DA] disabled:opacity-60 transition-all"
          >
            <ImageIcon size={16} /> {photoSaving ? "Setting…" : `Use this photo as ${(dogs.find((d) => d.id === dogId)?.name) || "dog"}'s profile picture`}
          </button>
        )
      )}

      <p className="flex items-start gap-1.5 text-xs text-[#9A938B] mt-4">
        <Dna size={13} className="mt-0.5 shrink-0" />
        Estimate from the photo only — a DNA test is the only way to confirm breed for certain.
      </p>

      {/* Breed-specific care tips shortcut */}
      {!isUnclear && (
        tips === null ? (
          <button
            data-testid="breed-get-tips-btn"
            onClick={loadTips}
            disabled={tipsLoading}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F8F5F0] text-[#B54828] font-extrabold px-5 py-3 hover:bg-[#F2EFE9] disabled:opacity-60 transition-all"
          >
            {tipsLoading ? <><Spinner size={18} /> Fetching tips…</> : <><Lightbulb size={16} /> Get {result.primary_breed} care tips</>}
          </button>
        ) : (
          <div className="mt-4 bg-[#FBF6EE] rounded-2xl p-4 border border-[#E8E3DA]" data-testid="breed-tips-expanded">
            <p className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#B54828] mb-2">
              <Lightbulb size={14} /> {result.primary_breed} care tips
            </p>
            {tips.length > 0 ? (
              <ul className="space-y-1.5">
                {tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#2D2B2A]">
                    <Check size={15} color="#E86A45" className="mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#9A938B]">Tips aren&apos;t available right now — check back soon.</p>
            )}
          </div>
        )
      )}
    </div>
  );
}
